import {
  Download,
  EllipsisVertical,
  Eye,
  File,
  FilePlus2,
  FileText,
  Image,
  LayoutList,
  Loader2,
  MonitorCog,
  Receipt,
  Stethoscope,
  Trash2,
  Upload,
} from 'lucide-react'
import moment from 'moment'
import React, {useEffect, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {
  useDeleteMedicalEnquiryDocument,
  useMedicalEnquiry,
  useUploadMedicalEnquiryFiles,
} from '../../../../hooks/useMedicalEnquiry'
import {
  downloadMedicalEnquiryDocument,
  generateMedicalEnquiryDocument,
} from '../../../../services/medicalEnquiryService'
import {MedicalEnquiryItem} from '../../../../types/medicalEnquiry'
import {useAuth} from '../../../modules/auth'
import './../style.css'

interface MedicalEnquiryProps {
  claimDetails: any
}
type ResponseAlert = {
  enquiryId: number
  type: 'success' | 'danger'
  message: string
} | null

const MedicalEnquiry: React.FC<MedicalEnquiryProps> = ({claimDetails}) => {
  const {formatMessage: f} = useIntl()
  const {currentUser} = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploadingToId, setUploadingToId] = useState<number | null>(null)
  const [viewModalOpen, setViewModalOpen] = useState<boolean>(false)
  const [viewingDocument, setViewingDocument] = useState<{
    fileName: string
    base64Data: string
    contentType: string
  } | null>(null)
  const [viewerType, setViewerType] = useState<'browser' | 'google'>('browser')
  const googleDocsBlobUrlRef = React.useRef<string | null>(null)

  const claimNumber = claimDetails?.claimDetails?.claim?.claimNumber
  const {medicalEnquiries, isLoading, error, refetch} = useMedicalEnquiry(claimNumber)
  const uploadFilesMutation = useUploadMedicalEnquiryFiles()
  const deleteDocumentMutation = useDeleteMedicalEnquiryDocument()

  const [responseAlert, setResponseAlert] = useState<ResponseAlert>(null)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, enquiryId: number) => {
    const files = Array.from(event.target.files || [])
    if (files.length > 0) {
      setSelectedFiles(files)
      handleFileUpload(files, enquiryId)
    }
  }

  useEffect(() => {
    if (!responseAlert) return

    const timer = setTimeout(() => {
      setResponseAlert(null)
    }, 3000)

    return () => clearTimeout(timer)
  }, [responseAlert])

  console.log('Medical Enquiries: ', currentUser)

  const handleFileUpload = async (files: File[], enquiryId: number) => {
    if (!currentUser || !claimNumber) return

    setUploadingToId(enquiryId)
    setResponseAlert(null)

    try {
      await uploadFilesMutation.mutateAsync({
        idMe: enquiryId,
        claimNumber,
        username: currentUser.username || 'User',
        files,
      })

      setResponseAlert({
        enquiryId,
        type: 'success',
        message: 'Files uploaded successfully!',
      })

      // console.log("ENQURY ID", enquiryId);

      setSelectedFiles([])
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (error) {
      console.error('Upload error:', error)
      toast.error('Failed to upload files')
    } finally {
      setUploadingToId(null)
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'medical':
        return <Stethoscope size={20} />
      case 'billing':
        return <Receipt size={20} />
      case 'administrative':
        return <FileText size={20} />
      case 'monitoring':
        return <MonitorCog size={20} />
      case 'additional documents':
        return <FilePlus2 size={20} />
      case 'general':
        return <LayoutList size={20} />
      default:
        return <FileText size={20} />
    }
  }

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase()
    switch (extension) {
      case 'pdf':
        return <FileText className='text-danger' size={16} />
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Image className='text-primary' size={16} />
      default:
        return <File className='text-secondary' size={16} />
    }
  }

  const formatDate = (dateString: string) => {
    return moment(dateString).format('DD MMM YYYY, HH:mm')
  }

  const handleDownloadEnquiry = async (enquiryId: number) => {
    if (!claimNumber) return

    try {
      const blob = await generateMedicalEnquiryDocument(claimNumber, enquiryId)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `MedicalEnquiry_${claimNumber}_${enquiryId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Document downloaded successfully!')
    } catch (error) {
      console.error('Download error:', error)
      toast.error('Failed to download document')
    }
  }

  const handleDownloadDocument = async (fileName: string) => {
    if (!claimNumber) return

    try {
      const response = await downloadMedicalEnquiryDocument(claimNumber, fileName)

      // Convert base64 to blob
      const byteCharacters = atob(response.base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], {type: response.contentType})

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = response.fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Document downloaded successfully!')
    } catch (error) {
      console.error('Download error:', error)
      toast.error('Failed to download document')
    }
  }

  const handleViewDocument = async (fileName: string) => {
    if (!claimNumber) return

    try {
      const response = await downloadMedicalEnquiryDocument(claimNumber, fileName)

      // Set the document data for modal viewing
      setViewingDocument({
        fileName: response.fileName,
        base64Data: response.base64Data,
        contentType: response.contentType,
      })
      setViewModalOpen(true)
    } catch (error) {
      console.error('View error:', error)
      toast.error('Failed to view document')
    }
  }

  const handleDeleteDocument = async (idDoc: number, enquiryId: number) => {
    if (!claimNumber || !currentUser) return

    setResponseAlert(null)

    try {
      await deleteDocumentMutation.mutateAsync({
        claimNumber,
        idDoc,
        username: currentUser.username || 'User',
      })

      console.log('ENQURY ID', enquiryId)

      setResponseAlert({
        enquiryId,
        type: 'success',
        message: 'Document deleted successfully!',
      })
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Failed to delete document')
    }
  }

  const closeViewModal = () => {
    setViewModalOpen(false)
    setViewingDocument(null)
    setViewerType('browser')
    if (googleDocsBlobUrlRef.current) {
      URL.revokeObjectURL(googleDocsBlobUrlRef.current)
      googleDocsBlobUrlRef.current = null
    }
  }

  const getFileExtension = (fileName: string) => {
    return fileName.split('.').pop()?.toLowerCase() || ''
  }

  const getDataUrl = (base64Data: string, contentType: string, fileName: string) => {
    const extension = getFileExtension(fileName)

    // Handle different content types and file extensions
    if (contentType === 'application/pdf' || extension === 'pdf') {
      return `data:application/pdf;base64,${base64Data}`
    } else if (
      contentType.startsWith('image/') ||
      ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)
    ) {
      return `data:${contentType || `image/${extension}`};base64,${base64Data}`
    } else if (contentType === 'application/octet-stream') {
      // For octet-stream, try to determine the actual type from file extension
      if (extension === 'pdf') {
        return `data:application/pdf;base64,${base64Data}`
      } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
        return `data:image/${extension};base64,${base64Data}`
      } else if (['doc', 'docx'].includes(extension)) {
        return `data:application/msword;base64,${base64Data}`
      } else if (['xls', 'xlsx'].includes(extension)) {
        return `data:application/vnd.ms-excel;base64,${base64Data}`
      } else {
        return `data:${contentType};base64,${base64Data}`
      }
    } else {
      return `data:${contentType};base64,${base64Data}`
    }
  }

  const getGoogleDocsViewerUrl = (base64Data: string, fileName: string) => {
    // Revoke any previously allocated blob URL before creating a new one
    if (googleDocsBlobUrlRef.current) {
      URL.revokeObjectURL(googleDocsBlobUrlRef.current)
    }
    const byteCharacters = atob(base64Data)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray])
    const blobUrl = URL.createObjectURL(blob)
    googleDocsBlobUrlRef.current = blobUrl

    // Google Docs Viewer URL
    return `https://docs.google.com/gview?url=${encodeURIComponent(blobUrl)}&embedded=true`
  }

  if (isLoading) {
    return (
      <div
        className='d-flex justify-content-center align-items-center'
        style={{minHeight: '200px'}}
      >
        <div className='d-flex align-items-center gap-2'>
          <Loader2 className='animate-spin' size={16} />
          <span>Loading medical enquiries...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='alert alert-danger'>
        <strong>Error:</strong> Failed to load medical enquiries. Please try again.
        <button className='btn btn-sm btn-outline-danger ms-2' onClick={() => refetch()}>
          Retry
        </button>
      </div>
    )
  }

  if (!medicalEnquiries || medicalEnquiries.length === 0) {
    return (
      <div className='text-center py-5'>
        <div className='text-muted'>
          <i className='fas fa-clipboard-list fa-3x mb-3'></i>
          <p>No medical enquiries found for this claim.</p>
        </div>
      </div>
    )
  }

  return (
    <div className='documents-container'>
      {medicalEnquiries.map((enquiry: MedicalEnquiryItem) => (
        <div key={enquiry.id} className='document-item'>
          <div className='document-header'>
            <div className='document-header-content'>
              <div className='document-icon success'>{getCategoryIcon(enquiry.category)}</div>
              <div className='document-text'>
                {/* <div className='fw-bold text-primary'>{enquiry.category}</div> */}
                <small className='text-muted'>
                  Advisor: <strong>{enquiry.medicalAdvisor}</strong>
                </small>
              </div>
              <div className='text-end'>
                <small className='fw-bold text-primary'>Category: {enquiry.category}</small>
              </div>
            </div>
          </div>

          <div className='document-files-list'>
            <h6 className='mb-3'>Enquiry</h6>

            {/* ================= QUESTION CARD ================= */}
            <div className='p-3 rounded border bg-light mb-3 w-100'>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '12px',
                }}
              >
                <div
                  style={{
                    lineHeight: '1.5',
                    fontSize: '0.95rem',
                    color: '#495057',
                    maxHeight: '220px',
                    overflowY: 'auto',
                    flex: 1,
                  }}
                >
                  <ol
                    style={{
                      listStyleType: 'decimal',
                      paddingLeft: '1.25rem',
                      marginBottom: 0,
                    }}
                  >
                    {enquiry.message
                      .split('[NEXT QUESTION]')
                      .map((q) => q.trim())
                      .filter(Boolean)
                      .map((question, index) => (
                        <li key={index} style={{marginBottom: '0.5rem'}}>
                          {question}
                        </li>
                      ))}
                  </ol>
                </div>

                <button
                  className='btn btn-sm btn-outline-primary flex-shrink-0'
                  onClick={() => handleDownloadEnquiry(enquiry.id)}
                  title='Download Medical Enquiry'
                >
                  <Download size={16} />
                </button>
              </div>
            </div>

            {/* ================= SUPPORTING DOCUMENT CARD ================= */}
            <div className='p-3 rounded border bg-light w-100'>
              <div className='d-flex align-items-center mb-2'>
                <i className='fas fa-paperclip text-muted me-2'></i>
                <h6 className='mb-0'>
                  Supporting Documents
                  <span className='text-muted fw-normal ms-1'>
                    ({enquiry.supportingDocument?.length || 0})
                  </span>
                </h6>
              </div>

              {enquiry.supportingDocument && enquiry.supportingDocument.length > 0 ? (
                <div className='d-flex flex-column gap-2'>
                  {enquiry.supportingDocument.map((fileName, index) => (
                    <div
                      key={index}
                      className='border rounded bg-white p-2'
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      {/* LEFT CONTENT */}
                      <div className='d-flex align-items-center flex-grow-1 overflow-hidden gap-2'>
                        <span className='file-icon me-2'>{getFileIcon(fileName)}</span>

                        <div className='overflow-hidden'>
                          <div className='fw-medium text-truncate'>{fileName}</div>
                          <small className='text-muted'>{formatDate(enquiry.createdAt)}</small>
                        </div>
                      </div>

                      {/* RIGHT ACTION — SEJAJAR DENGAN QUESTION BUTTON */}
                      <div className='dropdown ms-auto'>
                        <button
                          className='btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center'
                          type='button'
                          data-bs-toggle='dropdown'
                          aria-expanded='false'
                        >
                          <EllipsisVertical width={16} height={16} />
                        </button>

                        <ul className='dropdown-menu dropdown-menu-end'>
                          <li>
                            <button
                              className='dropdown-item d-flex align-items-center gap-2'
                              onClick={() => handleDownloadDocument(fileName)}
                            >
                              <Download size={14} />
                              Download
                            </button>
                          </li>

                          <li>
                            <button
                              className='dropdown-item d-flex align-items-center gap-2'
                              onClick={() => handleViewDocument(fileName)}
                            >
                              <Eye size={14} />
                              View
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='text-center py-3 border rounded bg-white'>
                  <p className='text-muted mb-0'>No supporting documents</p>
                </div>
              )}
            </div>
          </div>

          <div className='document-files-list'>
            <div className='mb-3'>
              <h6 className='mb-2'>
                {/* <i className='fas fa-paperclip me-1'></i> */}
                Responses ({enquiry.documents.length})
              </h6>
              {responseAlert?.enquiryId === enquiry.id && (
                <div className={`alert alert-${responseAlert.type} py-2`} role='alert'>
                  {responseAlert.message}
                </div>
              )}

              {enquiry.documents.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: '10px',
                  }}
                >
                  {enquiry.documents.map((doc) => (
                    <div
                      key={doc.id}
                      className='document-file-item'
                      style={{
                        maxWidth:
                          medicalEnquiries.indexOf(enquiry) === medicalEnquiries.length - 1
                            ? '350px'
                            : 'auto',
                      }}
                    >
                      <span className='file-icon'>{getFileIcon(doc.documentName)}</span>
                      <div className='ms-2 flex-grow-1'>
                        <div className='fw-medium text-truncate' style={{maxWidth: '140px'}}>
                          {doc.documentName}
                        </div>
                        <small className='text-muted'>
                          {/* {formatDate(doc.createdAt)} by {doc.createdBy} */}
                          {formatDate(doc.createdAt)}
                        </small>
                      </div>
                      <button
                        className='btn btn-sm'
                        style={{cursor: 'pointer'}}
                        onClick={() => handleDownloadDocument(doc.documentName)}
                        title='Download Document'
                      >
                        <Download size={16} />
                      </button>
                      <button
                        className='btn btn-sm'
                        style={{cursor: 'pointer'}}
                        onClick={() => handleViewDocument(doc.documentName)}
                        title='View Document'
                      >
                        <Eye size={16} />
                      </button>
                      {medicalEnquiries.indexOf(enquiry) === medicalEnquiries.length - 1 && (
                        <button
                          className='btn btn-sm btn-outline-danger flex-shrink-0'
                          style={{cursor: 'pointer'}}
                          onClick={() => handleDeleteDocument(doc.id, enquiry.id)}
                          title='Delete Document'
                          disabled={deleteDocumentMutation.isLoading}
                        >
                          {deleteDocumentMutation.isLoading ? (
                            <Loader2 className='animate-spin' size={16} />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className='text-center py-3 border rounded bg-light'>
                  <i className='fas fa-file-upload fa-2x text-muted mb-2'></i>
                  <p className='text-muted mb-2'>Please upload response documents enquiry here.</p>
                </div>
              )}

              {/* Upload Section - Show only for latest index if no documents exist */}
              {medicalEnquiries.indexOf(enquiry) === medicalEnquiries.length - 1 && (
                <div className='mt-3 pt-3 border-top'>
                  <div className='d-flex align-items-center gap-2'>
                    <input
                      ref={fileInputRef}
                      type='file'
                      multiple
                      accept='.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx'
                      onChange={(e) => handleFileSelect(e, enquiry.id)}
                      style={{display: 'none'}}
                    />
                    {medicalEnquiries.indexOf(enquiry) === medicalEnquiries.length - 1 && (
                      <button
                        className='upload-button'
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingToId === enquiry.id}
                      >
                        {uploadingToId === enquiry.id ? (
                          <>
                            <Loader2 className='animate-spin upload-icon' size={16} />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className='upload-icon' size={16} />
                            Upload Documents
                          </>
                        )}
                      </button>
                    )}
                    {/* <small className='text-muted'>Supports: PDF, JPG, PNG, GIF, DOC, DOCX</small> */}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      <style>
        {`
          .documents-container {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
            margin: 0 auto;
            padding: 12px;
          }

          .document-item {
            background: white;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
            overflow: hidden;
            border: 1px solid #e9ecef;
            display: flex;
            flex-direction: column;
            height: 100%;
            transition: all 0.2s ease;
          }
          
          .document-item.disabled {
            background: #f8f9fa;
            opacity: 0.75;
            cursor: not-allowed;
          }

          /* Responsive adjustments */
          @media (max-width: 992px) {
            .documents-container {
              grid-template-columns: repeat(2, 1fr);
            }
          }

          @media (max-width: 576px) {
            .documents-container {
              grid-template-columns: 1fr;
            }
          }

          .document-header {
            padding: 10px 12px;
            background: #ffffff;
            border-bottom: 1px solid #f1f3f5;
            cursor: pointer;
          }

          .document-header.disabled {
            background: #f8f9fa;
            cursor: not-allowed;
          }

          .document-header-content {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
            padding: 4px;
          }

          .document-text {
            font-weight: 500;
            color: #2c3e50;
            font-size: 13px;
            flex: 1;
          }

          .document-icon {
            font-size: 16px;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
          }

          .document-icon.success {
            color: #20C997;
            background: rgba(32, 201, 151, 0.1);
          }

          .document-icon.warning {
            color: #FD7E14;
            background: rgba(253, 126, 20, 0.1);
          }

          .document-icon.request {
            color: #FA5252;
            background: rgba(250, 82, 82, 0.1);
          }

          .document-files-list {
            padding: 8px 12px;
            background: white;
            flex-direction: column;
          }

          .document-file-item {
            display: flex;
            align-items: center;
            padding: 8px;
            border-radius: 4px;
            position: relative;
            cursor: pointer;
            // max-width: 320px;
          }

          .document-file-item:hover {
            background-color: #f5f5f5;
          }

          .upload-button {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 6px 12px;
            border-radius: 4px;
            background: rgb(5, 155, 182);
            color: white;
            border: 1px solid #dee2e6;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            margin-left: auto;
          }

          .upload-button:hover {
            background: rgb(0, 123, 145);
            border-color: #ced4da;
          }

          .upload-button.disabled {
            background: #e9ecef;
            color: #adb5bd;
            cursor: not-allowed;
            border-color: #dee2e6;
          }
          
          .upload-button.disabled:hover {
            background: #e9ecef;
          }

          .upload-icon {
            font-size: 14px;
            color: white;
          }

          @media (max-width: 768px) {
            .documents-container {
              padding: 8px;
            }

            .document-header,
            .document-files-list {
              padding: 8px;
            }

            .document-file-item {
              padding: 8px;
              gap: 8px;
            }
          }
        `}
      </style>

      {/* Document View Modal */}
      <Modal
        show={viewModalOpen}
        onHide={closeViewModal}
        size='xl'
        centered
        backdrop={true}
        keyboard={true}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <File size={20} className='me-2' />
            {viewingDocument?.fileName}
          </Modal.Title>
          {/* <div className='d-flex align-items-center gap-2'>
            <div className='btn-group btn-group-sm' role='group'>
              <button
                type='button'
                className={`btn ${
                  viewerType === 'browser' ? 'btn-primary' : 'btn-outline-primary'
                }`}
                onClick={() => setViewerType('browser')}
              >
                <i className='fas fa-globe me-1'></i>
                Browser
              </button>
              <button
                type='button'
                className={`btn ${viewerType === 'google' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setViewerType('google')}
              >
                <i className='fab fa-google me-1'></i>
                Google Docs
              </button>
            </div>
          </div> */}
        </Modal.Header>
        <Modal.Body style={{padding: 0, height: '80vh'}}>
          {viewingDocument && (
            <>
              {viewerType === 'google' ? (
                <iframe
                  src={getGoogleDocsViewerUrl(viewingDocument.base64Data, viewingDocument.fileName)}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title={`Google Docs Viewer - ${viewingDocument.fileName}`}
                />
              ) : (
                (() => {
                  const extension = getFileExtension(viewingDocument.fileName)
                  const dataUrl = getDataUrl(
                    viewingDocument.base64Data,
                    viewingDocument.contentType,
                    viewingDocument.fileName
                  )

                  if (viewingDocument.contentType === 'application/pdf' || extension === 'pdf') {
                    return (
                      <iframe
                        src={dataUrl}
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none',
                        }}
                        title={viewingDocument.fileName}
                      />
                    )
                  } else if (
                    viewingDocument.contentType.startsWith('image/') ||
                    ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)
                  ) {
                    return (
                      <div className='d-flex justify-content-center align-items-center h-100'>
                        <img
                          src={dataUrl}
                          alt={viewingDocument.fileName}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </div>
                    )
                  } else if (viewingDocument.contentType === 'application/octet-stream') {
                    // For octet-stream files, try browser first, then show fallback
                    if (['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
                      if (extension === 'pdf') {
                        return (
                          <iframe
                            src={dataUrl}
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none',
                            }}
                            title={viewingDocument.fileName}
                          />
                        )
                      } else {
                        return (
                          <div className='d-flex justify-content-center align-items-center h-100'>
                            <img
                              src={dataUrl}
                              alt={viewingDocument.fileName}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                              }}
                            />
                          </div>
                        )
                      }
                    } else {
                      // For other octet-stream files, show fallback with Google Docs option
                      return (
                        <div className='d-flex flex-column justify-content-center align-items-center h-100 text-center p-4'>
                          <i className='fas fa-file fa-4x text-muted mb-3'></i>
                          <h5 className='text-muted'>Preview not available in browser</h5>
                          <p className='text-muted mb-3'>
                            This file type ({viewingDocument.contentType}) cannot be previewed
                            directly in the browser.
                          </p>
                          <div className='d-flex gap-2'>
                            <button
                              className='btn btn-primary'
                              onClick={() => setViewerType('google')}
                            >
                              <i className='fab fa-google me-2'></i>
                              Try Google Docs Viewer
                            </button>
                            <button
                              className='btn btn-outline-primary'
                              onClick={() => {
                                // Convert base64 to blob and download
                                const byteCharacters = atob(viewingDocument.base64Data)
                                const byteNumbers = new Array(byteCharacters.length)
                                for (let i = 0; i < byteCharacters.length; i++) {
                                  byteNumbers[i] = byteCharacters.charCodeAt(i)
                                }
                                const byteArray = new Uint8Array(byteNumbers)
                                const blob = new Blob([byteArray], {
                                  type: viewingDocument.contentType,
                                })
                                const url = window.URL.createObjectURL(blob)
                                const link = document.createElement('a')
                                link.href = url
                                link.download = viewingDocument.fileName
                                document.body.appendChild(link)
                                link.click()
                                document.body.removeChild(link)
                                window.URL.revokeObjectURL(url)
                              }}
                            >
                              <i className='fas fa-download me-2'></i>
                              Download File
                            </button>
                          </div>
                        </div>
                      )
                    }
                  } else {
                    // For other file types, show fallback
                    return (
                      <div className='d-flex flex-column justify-content-center align-items-center h-100 text-center p-4'>
                        <i className='fas fa-file fa-4x text-muted mb-3'></i>
                        <h5 className='text-muted'>Preview not available</h5>
                        <p className='text-muted mb-3'>
                          This file type ({viewingDocument.contentType}) cannot be previewed in the
                          browser.
                        </p>
                        <div className='d-flex gap-2'>
                          <button
                            className='btn btn-primary'
                            onClick={() => setViewerType('google')}
                          >
                            <i className='fab fa-google me-2'></i>
                            Try Google Docs Viewer
                          </button>
                          <button
                            className='btn btn-outline-primary'
                            onClick={() => {
                              // Convert base64 to blob and download
                              const byteCharacters = atob(viewingDocument.base64Data)
                              const byteNumbers = new Array(byteCharacters.length)
                              for (let i = 0; i < byteCharacters.length; i++) {
                                byteNumbers[i] = byteCharacters.charCodeAt(i)
                              }
                              const byteArray = new Uint8Array(byteNumbers)
                              const blob = new Blob([byteArray], {
                                type: viewingDocument.contentType,
                              })
                              const url = window.URL.createObjectURL(blob)
                              const link = document.createElement('a')
                              link.href = url
                              link.download = viewingDocument.fileName
                              document.body.appendChild(link)
                              link.click()
                              document.body.removeChild(link)
                              window.URL.revokeObjectURL(url)
                            }}
                          >
                            <i className='fas fa-download me-2'></i>
                            Download File
                          </button>
                        </div>
                      </div>
                    )
                  }
                })()
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-secondary' onClick={closeViewModal}>
            Close
          </button>
          <button
            type='button'
            className='btn btn-primary'
            onClick={() => {
              if (viewingDocument) {
                // Download the document
                const byteCharacters = atob(viewingDocument.base64Data)
                const byteNumbers = new Array(byteCharacters.length)
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i)
                }
                const byteArray = new Uint8Array(byteNumbers)
                const blob = new Blob([byteArray], {type: viewingDocument.contentType})
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = viewingDocument.fileName
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
              }
            }}
          >
            <Download size={16} className='me-2' />
            Download
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default MedicalEnquiry
